import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, ChevronRight, CheckCircle2, AlertTriangle, Clock, Loader2 } from "lucide-react";

const STATUS_LABEL = {
    sent: "Sent",
    viewed: "Viewed",
    paid: "Paid",
    overdue: "Overdue",
    void: "Void",
};

const STATUS_COLOR = {
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    viewed: "bg-violet-50 text-violet-800 ring-violet-200",
    paid: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    void: "bg-slate-200 text-slate-700 ring-slate-300",
};

const OUTSTANDING_SET = new Set(["sent", "viewed", "overdue"]);
// Phase STATE-DRIFT-SWEEP — single source of visual truth for the
// invoice badge + Pay-now gate. Handles `partial_paid` and the
// cron-lag past-due window that STATUS_COLOR/LABEL above missed.
import { resolveInvoiceStatus } from "@/lib/statusResolvers";

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }

export default function CustomerInvoicesListPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = searchParams.get("filter") || "all";

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/customer/invoices");
                if (!cancelled) setItems(data.items || []);
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const filtered = items.filter((inv) => {
        // Phase INV-HIDE-VOID (2026-07-10) — defence-in-depth: even
        // though the backend excludes voided invoices from the customer
        // list, hide them client-side too so a cached/stale response
        // never leaks admin-only bookkeeping artifacts.
        if (inv.status === "void") return false;
        // Phase STATE-DRIFT-SWEEP — use the resolver so `partial_paid`
        // and cron-lag past-due surface correctly under "Outstanding".
        const s = resolveInvoiceStatus(inv);
        if (filter === "outstanding") return s.isPayable;
        if (filter === "paid") return s.isPaid;
        return true;
    });

    // Default sort: issue_date desc (server already sorts this way).
    // Outstanding total also filters voids for the same defence-in-depth reason.
    const totalOutstandingCents = items
        .filter((i) => i.status !== "void" && resolveInvoiceStatus(i).isPayable)
        .reduce((sum, i) => sum + (i.total_cents || 0), 0);

    const setFilter = (next) => {
        const sp = new URLSearchParams(searchParams);
        if (next === "all") sp.delete("filter");
        else sp.set("filter", next);
        setSearchParams(sp, { replace: true });
    };

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Billing</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">My invoices</h1>
                    <p className="text-sm text-slate-500 mt-1">View, download, and pay invoices online via bank transfer.</p>
                </div>
                <div className="text-right">
                    <p className="text-3xl font-display font-bold text-slate-900 tabular-nums"
                       data-testid="customer-invoices-outstanding-total">
                        {fmtMoney(totalOutstandingCents)}
                    </p>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Outstanding</p>
                </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="customer-invoices-chips">
                {[
                    { value: "all", label: "All", icon: Receipt },
                    { value: "outstanding", label: "Outstanding", icon: Clock },
                    { value: "paid", label: "Paid", icon: CheckCircle2 },
                ].map(({ value, label, icon: Icon }) => {
                    const active = filter === value;
                    return (
                        <button
                            key={value}
                            onClick={() => setFilter(value)}
                            data-testid={`customer-invoices-chip-${value}`}
                            className={
                                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                                (active
                                    ? "bg-blue-900 text-white"
                                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50")
                            }
                        >
                            <Icon size={12} /> {label}
                        </button>
                    );
                })}
            </div>

            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="customer-invoices-loading">Loading…</p>}
                    {!loading && filtered.length === 0 && (
                        <div className="p-12 text-center" data-testid="customer-invoices-empty">
                            <div className="mx-auto h-12 w-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400">
                                <Receipt size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No invoices yet</h3>
                            <p className="mt-2 text-sm text-slate-600">
                                When we issue an invoice you'll find it right here.
                            </p>
                        </div>
                    )}
                    {!loading && filtered.length > 0 && (
                        <ul className="divide-y divide-slate-100">
                            {filtered.map((inv) => {
                                // Phase STATE-DRIFT-SWEEP — resolver
                                // supersedes the previous `outstanding =
                                // OUTSTANDING_SET.has(inv.status)` +
                                // inline STATUS_COLOR/LABEL logic. Now
                                // handles `partial_paid` + cron-lag
                                // past-due detection.
                                const s = resolveInvoiceStatus(inv);
                                const outstanding = s.isPayable;
                                return (
                                    <li key={inv.id} data-testid={`customer-invoices-row-${inv.id}`}
                                        className="p-4 sm:p-5 hover:bg-slate-50/40 transition-colors">
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-mono text-slate-900 font-semibold">{inv.invoice_number}</span>
                                                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${s.tone}`}
                                                          data-testid={`customer-invoice-status-${inv.id}`}>
                                                        {s.label}
                                                    </span>
                                                    {s.isOverdue && (
                                                        <span className="inline-flex items-center gap-1 text-[11px] text-red-700">
                                                            <AlertTriangle size={11} /> Past due
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xl font-display font-bold text-slate-900 tabular-nums">
                                                    {fmtMoney(inv.total_cents)}
                                                </p>
                                                <div className="mt-2 flex gap-2 justify-end">
                                                    <Button asChild size="sm" variant="outline" className="gap-1.5">
                                                        <Link to={`/customer/invoices/${inv.id}`}
                                                              data-testid={`customer-invoices-open-${inv.id}`}>
                                                            View <ChevronRight size={14} />
                                                        </Link>
                                                    </Button>
                                                    {outstanding && inv.payment_in_flight && (
                                                        <span
                                                            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-amber-50 text-amber-800 ring-1 ring-amber-200"
                                                            data-testid={`customer-invoices-processing-${inv.id}`}
                                                        >
                                                            <Loader2 size={12} className="animate-spin" /> Processing
                                                        </span>
                                                    )}
                                                    {outstanding && !inv.payment_in_flight && (
                                                        <Button asChild size="sm" className="bg-blue-900 hover:bg-blue-800">
                                                            <Link to={`/customer/invoices/${inv.id}?pay=1`}
                                                                  data-testid={`customer-invoices-pay-${inv.id}`}>
                                                                Pay now
                                                            </Link>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </DashboardShell>
    );
}
